/**
 * Page Context
 *
 * Provides current page metadata and data to the chat assistant.
 * Pages can set context that will be included in chat requests for better answers.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

export interface PageContext {
  route?: string;
  title?: string;
  data?: Record<string, unknown>;
}

interface PageContextValue {
  pageContext: PageContext | null;
  setPageContext: (context: PageContext | null) => void;
}

const PageContextContext = createContext<PageContextValue | undefined>(undefined);

export const PageContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pageContext, setPageContextState] = useState<PageContext | null>(null);

  const setPageContext = useCallback((context: PageContext | null) => {
    setPageContextState(context);
  }, []);

  const value = useMemo(() => ({ pageContext, setPageContext }), [pageContext, setPageContext]);

  return (
    <PageContextContext.Provider value={value}>
      {children}
    </PageContextContext.Provider>
  );
};

/**
 * Hook to access current page context (for ChatPanel)
 */
export function usePageContext() {
  const context = useContext(PageContextContext);
  if (!context) {
    throw new Error('usePageContext must be used within PageContextProvider');
  }
  return context;
}

/**
 * Hook for pages to set their context
 *
 * Usage:
 * ```tsx
 * useSetPageContext({
 *   route: '/taxes',
 *   title: 'Tax Estimates 2024',
 *   data: { totalTax: 45678.90, federalTax: 32100.50 }
 * });
 * ```
 */
export function useSetPageContext(context: PageContext | null) {
  const { setPageContext } = usePageContext();

  useEffect(() => {
    setPageContext(context);

    // Clear context when component unmounts (user navigates away)
    return () => {
      setPageContext(null);
    };
  }, [context, setPageContext]);
}
